/**
 * Reports resource — typed wrappers over `/v1/twin/reports[/{id}]` plus a
 * server→UI mapper. Backs the Twin Report Generator's durable persistence
 * (B2.x), replacing the SPA's localStorage reports store in live mode.
 *
 * The server's `config` field is opaque JSON the UI lane owns — it *is* a
 * `ReportConfig` minus the identity fields, which live on the envelope. So
 * the mapper is near-identity: hydrate `config`, then let the envelope's
 * id/name/updated_at win. Persisted reports are always user-owned ("my") or
 * "shared" — pack templates stay client-side (seeded from fixtures).
 */

import { api } from "@/lib/api-client";
import type { ReportConfig } from "@/lib/reports/types";
import type { Report, ReportList } from "./types";

export const reports = {
  list: () => api.get<ReportList>("/v1/twin/reports"),
  get: (id: string) => api.get<Report>(`/v1/twin/reports/${id}`),
  /**
   * Create a report. `id` is optional + client-owned (the SPA's create/clone
   * are synchronous, so the store mints a UUID and the server upserts on it);
   * `slug` is optional — the server derives it from the name.
   */
  create: (body: { id?: string; name: string; slug?: string; config?: unknown }) =>
    api.post<Report>("/v1/twin/reports", body),
  /** Patch name and/or the opaque config blob. */
  update: (id: string, body: { name?: string; config?: unknown }) =>
    api.patch<Report>(`/v1/twin/reports/${id}`, body),
  /** Delete a report (204; idempotent server-side). */
  remove: (id: string) => api.delete<void>(`/v1/twin/reports/${id}`),
};

/**
 * Bridge the server envelope to the UI `ReportConfig`. Near-identity: the
 * config blob carries pages/narrative/filters/etc.; the envelope's
 * id/name/updated_at are authoritative.
 */
export function toReportConfig(r: Report): ReportConfig {
  const cfg = (r.config && typeof r.config === "object"
    ? (r.config as Partial<ReportConfig>)
    : {}) as Partial<ReportConfig>;

  return {
    ...cfg,
    id: r.id,
    name: r.name,
    description: cfg.description,
    pack: cfg.pack,
    templateId: cfg.templateId,
    source: cfg.source === "shared" ? "shared" : "my",
    owner: cfg.owner ?? "you",
    createdAt: cfg.createdAt ?? r.created_at,
    updatedAt: r.updated_at,
    pages: cfg.pages ?? [],
    narrative: cfg.narrative ?? {},
    filters: cfg.filters ?? {},
  } as ReportConfig;
}
